export const TRANSLATION_USAGE_TIMEZONE = 'Asia/Shanghai';
export const TRANSLATION_STATUS_FALLBACK_TIMEZONE = 'Europe/Berlin';
const MAX_RANGE_DAYS = 180;

export const getBrowserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || TRANSLATION_STATUS_FALLBACK_TIMEZONE;
  } catch {
    return TRANSLATION_STATUS_FALLBACK_TIMEZONE;
  }
};

export const TRANSLATION_RANGE_PRESETS = [
  {id: 'today', label: 'Today'},
  {id: 'yesterday', label: 'Yesterday'},
  {id: 'thisWeek', label: 'This week'},
  {id: 'last7Days', label: 'Last 7 days'},
  {id: 'thisMonth', label: 'This month'},
  {id: 'lastMonth', label: 'Last month'},
  {id: 'last30Days', label: 'Last 30 days'},
];

const toDateKey = (date, timeZone) => {
  const values = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date).reduce((result, part) => {
    result[part.type] = part.value;
    return result;
  }, {});
  return `${values.year}-${values.month}-${values.day}`;
};

const shiftDateKey = (key, days) => {
  const date = new Date(`${key}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

export const getPresetRange = (preset, now = new Date(), timeZone = TRANSLATION_USAGE_TIMEZONE) => {
  const to = toDateKey(now, timeZone);
  // Use the requested local calendar date; UTC arithmetic avoids DST-length days.
  const date = new Date(`${to}T00:00:00.000Z`);
  switch (preset) {
    case 'today': return {from: to, to};
    case 'yesterday': {
      const yesterday = shiftDateKey(to, -1);
      return {from: yesterday, to: yesterday};
    }
    case 'thisWeek': return {from: shiftDateKey(to, -((date.getUTCDay() + 6) % 7)), to};
    case 'last7Days': return {from: shiftDateKey(to, -6), to};
    case 'thisMonth': return {from: `${to.slice(0, 7)}-01`, to};
    case 'lastMonth': {
      date.setUTCDate(1);
      date.setUTCMonth(date.getUTCMonth() - 1);
      return {from: date.toISOString().slice(0, 10), to: shiftDateKey(`${to.slice(0, 7)}-01`, -1)};
    }
    case 'last30Days': return {from: shiftDateKey(to, -29), to};
    default: throw new RangeError('Unknown translation date range.');
  }
};

export const getDefaultRange = (now = new Date(), timeZone = TRANSLATION_USAGE_TIMEZONE) =>
  getPresetRange('last30Days', now, timeZone);

const isDateKey = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.valueOf()) && date.toISOString().slice(0, 10) === value;
};

export const validateRange = (range, now = new Date(), timeZone = TRANSLATION_USAGE_TIMEZONE) => {
  const {from, to} = range || {};
  if (!isDateKey(from) || !isDateKey(to)) return 'Select both dates in YYYY-MM-DD format.';
  if (from > to) return 'The start date must be on or before the end date.';
  const today = toDateKey(now, timeZone);
  if (from > today || to > today) return 'Dates cannot be in the future.';
  const days = Math.round(
    (new Date(`${to}T00:00:00.000Z`) - new Date(`${from}T00:00:00.000Z`)) / 86400000
  ) + 1;
  return days > MAX_RANGE_DAYS ? `Select no more than ${MAX_RANGE_DAYS} days.` : '';
};

export const formatUsd = (micros) => {
  if (typeof micros !== 'number' || !Number.isFinite(micros)) return '—';
  const dollars = micros / 1000000;
  return dollars > 0 && dollars < 0.01
    ? `$${dollars.toLocaleString('en-US', {minimumFractionDigits: 4, maximumFractionDigits: 6})}`
    : `$${dollars.toFixed(2)}`;
};
